/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import Head from 'next/head';
import type { SeoMetadataInfo } from '@/features/common';
import { metaTagKey } from '@/constants/seoMetaTags';

export type SeoMetaDataProps = {
  categoryImageUrl: string;
  seoMetaData: SeoMetadataInfo;
};

const isEmptySeoMetadata = (metadata: SeoMetadataInfo): boolean => {
  return Object.values(metadata).every((value) => {
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    return value === null || value === '' || value === true;
  });
};

export const SeoMetaData = ({ categoryImageUrl, seoMetaData }: SeoMetaDataProps) => {
  if (isEmptySeoMetadata(seoMetaData)) {
    return null;
  }

  return (
    <Head>
      <title key={metaTagKey('title')}>{seoMetaData.pageTitle}</title>
      {/* Open Graph Meta Tags */}
      {seoMetaData.pageTitle ? (
        <meta key={metaTagKey('og:title')} name="og:title" content={seoMetaData.pageTitle} />
      ) : null}

      {seoMetaData.indexEnabledFlag ? (
        <>
          {seoMetaData.currentPageUrl ? (
            <meta
              key={metaTagKey('og:url')}
              property="og:url"
              content={seoMetaData.currentPageUrl}
            />
          ) : null}
          {seoMetaData.pageContentDescription ? (
            <meta
              key={metaTagKey('description')}
              name="description"
              content={seoMetaData.pageContentDescription}
            />
          ) : null}
          {categoryImageUrl ? (
            <meta key={metaTagKey('og:image')} property="og:image" content={categoryImageUrl} />
          ) : null}
          {seoMetaData.siteName ? (
            <meta
              key={metaTagKey('og:site_name')}
              property="og:site_name"
              content={seoMetaData.siteName}
            />
          ) : null}
        </>
      ) : null}

      {seoMetaData.robotsContent ? (
        <meta key={metaTagKey('robots')} name="robots" content={seoMetaData.robotsContent} />
      ) : null}

      {/* Canonical URL Meta Tags */}
      {seoMetaData.canonicalUrl ? (
        <link key={metaTagKey('canonical')} rel="canonical" href={seoMetaData.canonicalUrl} />
      ) : null}

      {seoMetaData.alternateUrls?.map((alternateUrl) => {
        return (
          <link
            key={`${metaTagKey('alternateUrl')}_${alternateUrl.locale}`}
            rel="alternate"
            hrefLang={alternateUrl.locale}
            href={alternateUrl.url}
          />
        );
      })}
    </Head>
  );
};
